import { comparer, computed, IReactionDisposer, makeObservable, reaction } from 'mobx';

import { IApiClient } from '../../../app/data/client';
import { BasePageState } from '../../../app/shared/BasePageState';
import { debounce } from '../../../app/shared/common';
import { INavigationService } from '../../../app/shared/NavigationService';
import { Tab } from '../../../app/shared/Tabs/Tab';
import { Tabs } from '../../../app/shared/Tabs/Tabs';
import { PricelistsTranslation } from '../../localization/ModuleTranslation';
import { PricingContext } from '../../model';
import { ModuleRoutes } from '../../ModuleRoutes';
import PricelistListingState from '../PricelistListing/PricelistListingState';
import ProductListingState from '../ProductListing/ProductListingState';
import { PricelistsQueryParser } from './PricelistsQuery';

export const TAB_IDS = {
  Products: 'products',
  Pricelists: 'pricelists',
} as const;

export type TabId = (typeof TAB_IDS)[keyof typeof TAB_IDS];

const QUERY_DEBOUNCE_MS = 300;

export default class PricelistsState extends BasePageState<never> {
  public readonly tabs: Tabs;
  public readonly productListing: ProductListingState;
  public readonly pricelistListing: PricelistListingState;

  private disposeQueryReaction: IReactionDisposer;
  private disposeActiveTabReaction: IReactionDisposer;
  private isDisposed = false;

  /**
   * The page owns the URL because only the page knows which tab is showing, and the query holds one
   * tab's parameters at a time. Each tab still says what its own parameters are; the page adds `tab`
   * and writes the two together, so switching tabs leaves the other tab's parameters out rather than
   * having to delete them.
   */
  public constructor(
    client: IApiClient,
    pricingContext: PricingContext,
    translation: PricelistsTranslation,
    routes: ModuleRoutes,
    private readonly navigation: INavigationService,
  ) {
    super(translation);

    makeObservable(this);

    const query = navigation.currentUrl.query;
    const activeTabId =
      PricelistsQueryParser.toModel(query).tab === TAB_IDS.Pricelists ? TAB_IDS.Pricelists : TAB_IDS.Products;

    this.tabs = new Tabs([
      new Tab(TAB_IDS.Products, translation.tabs[TAB_IDS.Products], activeTabId === TAB_IDS.Products),
      new Tab(TAB_IDS.Pricelists, translation.tabs[TAB_IDS.Pricelists], activeTabId === TAB_IDS.Pricelists),
    ]);

    this.pricelistListing = new PricelistListingState(
      client,
      pricingContext,
      routes,
      translation.pricelistListing,
      this.getTabQuery(query, TAB_IDS.Pricelists),
    );
    this.productListing = new ProductListingState(
      client,
      pricingContext,
      translation.productListing,
      routes,
      this.getTabQuery(query, TAB_IDS.Products),
    );
  }

  @computed
  private get query(): Map<string, string> {
    const query = PricelistsQueryParser.toQuery({
      tab: this.tabs.isActiveTab(TAB_IDS.Pricelists) ? TAB_IDS.Pricelists : undefined,
    });
    const activeTabQuery = this.tabs.isActiveTab(TAB_IDS.Pricelists)
      ? this.pricelistListing.query
      : this.productListing.query;

    activeTabQuery.forEach((value, key) => query.set(key, value));

    return query;
  }

  /** Awaited by the page pipeline, so the page loader covers whatever the tabs wait for. */
  public async onLoad(): Promise<void> {
    await Promise.all([this.productListing.onLoad(), this.pricelistListing.onLoad()]);

    this.watchQuery();
    this.watchActiveTab();
  }

  public unload(): void {
    this.isDisposed = true;
    this.disposeQueryReaction?.();
    this.disposeActiveTabReaction?.();
    this.productListing.unload();
    this.pricelistListing.unload();
  }

  /**
   * The products tab can change the collections — adding products to one, or creating one from the
   * add modal — and the listing would otherwise still show what it fetched when the page opened. The
   * page reloads it on the way in rather than being told, so no mutation has to remember to say so.
   *
   * The cost is a request on every switch, even when nothing changed, and the list starting over at
   * the first page. The precise alternative is for the add modal to raise an `app/shared/Event` on
   * success and for this state to subscribe to it — fires only on a real change, but has to be
   * unsubscribed in `unload`, and still misses a collection changed outside this page.
   */
  private watchActiveTab(): void {
    this.disposeActiveTabReaction = reaction(
      () => this.tabs.isActiveTab(TAB_IDS.Pricelists),
      (isShowingPricelists) => {
        if (isShowingPricelists) {
          this.pricelistListing.reload();
        }
      },
    );
  }

  private watchQuery(): void {
    this.disposeQueryReaction = reaction(
      () => this.query,
      debounce(() => this.updateQuery(), QUERY_DEBOUNCE_MS),
      { equals: comparer.structural },
    );
  }

  private updateQuery(): void {
    // Disposing stops the reaction but cannot clear a write it has already scheduled, and that write
    // would land on whatever page the user moved to.
    if (this.isDisposed) {
      return;
    }

    this.navigation.setQuery(this.query);
  }

  private getTabQuery(query: Map<string, string>, tabId: TabId): Map<string, string> {
    if (this.tabs.isActiveTab(tabId)) {
      return query;
    }

    return new Map<string, string>();
  }
}
